import { useState, useEffect } from 'react'

import { Loader } from '@mantine/core'

import { Metadata, useQuery } from '@redwoodjs/web'

import { GET_PUBLIC_COMPANY } from 'src/graphql/companies.graphql'
import { Logo, Name } from 'src/icons'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'

const TermsOfServicePage = ({ companyId }) => {
  const [logoError, setLogoError] = useState(false)

  const { data: companyData, loading: companyLoading } = useQuery(
    GET_PUBLIC_COMPANY,
    {
      skip: !companyId,
      variables: {
        companyId: companyId,
      },
    }
  )

  useEffect(() => {
    if (!companyData?.publicCompany && !companyLoading) {
      window.location.href = '/404'
    }
  }, [companyData, companyLoading])

  if (companyLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <Loader size="sm" color="gray" />
      </div>
    )
  }

  if (!companyData?.publicCompany) {
    return null
  }

  const companyName = companyData.publicCompany.companyName
  const companyPhoneNumber = companyData.publicCompany.companyPhoneNumber
  const formattedPhone = formatInternationalPhoneNumber(companyPhoneNumber)
  const privacyUrl = `${window.location.origin}/privacy/${companyId}`
  const termsUrl = `${window.location.origin}/terms/${companyId}`

  return (
    <>
      <Metadata
        title={`${companyName} SMS Terms & Conditions`}
        description="SMS Terms & Conditions"
      />
      <div className="flex min-h-screen flex-col bg-white">
        <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Company Logo */}
          <div className="mb-8 flex justify-center">
            {logoError ? (
              <div className="flex flex-row gap-2">
                <img src={Logo} alt="Logo" className="h-12" />
                <img src={Name} alt="Name" className="h-12" />
              </div>
            ) : (
              <img
                src={`${process.env.AWS_PUBLIC_CDN_DOMAIN}/company/${companyId}/logo.png`}
                onError={() => {
                  setLogoError(true)
                }}
                alt={`${companyName} Logo`}
                className="h-12"
              />
            )}
          </div>

          {/* Terms & Conditions Content */}
          <div className="prose prose-lg max-w-none">
            <h1 className="mb-6 text-3xl font-bold text-gray-900">
              {companyName} Job Application SMS Terms & Conditions
            </h1>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                1. Program Description
              </h2>
              <p className="mb-4 text-gray-700">
                {companyName} sends transactional SMS updates to job applicants
                who optionally opt in via an unchecked checkbox on our
                application form. Opted-in applicants receive a confirmation
                text and application status notifications (e.g., application
                received, interview invitation, documents pending). Messages
                identify {companyName} and include opt-out instructions (Reply{' '}
                <strong>STOP</strong>). No marketing messages are sent, and we
                do not sell, rent, or share applicants&apos; phone numbers or
                SMS data with third parties for marketing.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                2. Opting Out
              </h2>
              <p className="mb-4 text-gray-700">
                You can cancel the SMS service at any time. Just text{' '}
                <strong>STOP</strong> to the short code. After you send the SMS
                message <strong>STOP</strong> to us, we will send you an SMS
                message to confirm that you have been unsubscribed. After this,
                you will no longer receive SMS messages from us. If you want to
                join again, just sign up as you did the first time and we will
                start sending SMS messages to you again.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                3. Getting Help
              </h2>
              <p className="mb-4 text-gray-700">
                If you are experiencing issues with the messaging program you
                can reply with the keyword <strong>HELP</strong> for more
                assistance
                {formattedPhone
                  ? `, or you can get help directly at ${formattedPhone}`
                  : ''}
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                4. Carrier Liability
              </h2>
              <p className="mb-4 text-gray-700">
                Carriers are not liable for delayed or undelivered messages.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                5. Message and Data Rates
              </h2>
              <p className="mb-4 text-gray-700">
                As always, message and data rates may apply for any messages
                sent to you from us and to us from you. You will receive
                messages sporadically throughout the recruiting process. If you
                have any questions about your text plan or data plan, it is best
                to contact your wireless provider.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                6. Privacy Policy
              </h2>
              <p className="mb-4 text-gray-700">
                If you have any questions regarding privacy, please read our
                privacy policy:{' '}
                <a href={privacyUrl} className="text-blue-600 hover:underline">
                  {privacyUrl}
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                Changes to our Terms & Conditions
              </h2>
              <p className="mb-4 text-gray-700">
                We may change these Terms & Conditions from time to time, and if
                we do, the most current version will be available at{' '}
                <a href={termsUrl} className="text-blue-600 hover:underline">
                  {termsUrl}
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  )
}

export default TermsOfServicePage
